import { soApi } from "@src/api/so";
import { lotsServices, type ApiStockLot } from "../../../../../api/lots";
import type { StockActInvContent } from "../../../../../api/stockActInvs";

/** Результат поиска строки АИ по скану партии. */
export type StockFullInventoryLotScanResult =
    | { type: "notFound" }
    | { type: "contentNotFound"; lot: ApiStockLot }
    | { type: "ambiguous"; lot: ApiStockLot; contents: StockActInvContent[] }
    | { type: "single"; content: StockActInvContent };

/**
 * Ищет партию по скану и сопоставляет её со строками состава текущего АИ.
 * @param barcode отсканированная этикетка партии
 * @param contents строки состава текущего АИ
 */
export async function resolveStockFullInventoryLotScan(
    barcode: string,
    contents: StockActInvContent[],
): Promise<StockFullInventoryLotScanResult> {
    const scannedObject = await soApi.findObject({ soNum: barcode });
    const lotsByCode = isValidCode(scannedObject.soCode)
        ? await lotsServices.load({ flt: { Codes: [scannedObject.soCode] } })
        : [];
    const lotsByText = await lotsServices.load({ flt: { SoNum: barcode } });
    const lots = uniqueLots([...lotsByCode, ...lotsByText]);

    if (lots.length === 0) {
        return { type: "notFound" };
    }

    const matchedContents = lots.flatMap((lot) =>
        contents.filter((content) => content.ParentLotCode === lot.LotCode).map((content) => ({ lot, content })),
    );

    if (matchedContents.length === 0) {
        return { type: "contentNotFound", lot: lots[0] };
    }

    if (matchedContents.length > 1) {
        return {
            type: "ambiguous",
            lot: matchedContents[0].lot,
            contents: matchedContents.map((item) => item.content),
        };
    }

    return { type: "single", content: matchedContents[0].content };
}

function uniqueLots(lots: ApiStockLot[]): ApiStockLot[] {
    const result: ApiStockLot[] = [];
    const seen = new Set<number>();

    lots.forEach((lot) => {
        if (!seen.has(lot.LotCode)) {
            seen.add(lot.LotCode);
            result.push(lot);
        }
    });

    return result;
}

function isValidCode(code: number): boolean {
    return code !== 0;
}
